import React from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Avatar for adults (co-owners, blocked users, friends, etc.).
 *
 * Prefers the user's profile photo (`profilePicture` URL) when present;
 * falls back to the project's ocean-blue + white initial circle. Use this
 * instead of a literal `👤` emoji fallback — emojis read as "unknown
 * person" rather than "this is a person we just don't have a photo for."
 *
 * Distinct from the global `<UserAvatar>` component (which falls back to
 * the user's emoji-avatar or a generic icon). This one always lands on
 * the initial-circle, which is the established Settings-modal pattern
 * (see PrivacyTab blocked-users list).
 *
 * @param {object} props
 * @param {string} [props.name]            used to derive the fallback initial
 * @param {string} [props.profilePicture]  URL to user_images binary endpoint
 * @param {'xs'|'sm'|'md'|'lg'} [props.size='md']
 */
function AdultAvatar({ name, profilePicture, size = 'md' }) {
  const { t } = useTranslation();
  const sizeClasses = {
    xs: 'w-5 h-5 text-[10px]',
    sm: 'w-8 h-8 text-sm',
    md: 'w-10 h-10 text-base',
    lg: 'w-12 h-12 text-lg'
  };
  const sizeClass = sizeClasses[size] || sizeClasses.md;

  if (profilePicture) {
    return (
      <img
        src={profilePicture}
        alt={name ? t('adultAvatar.photoAlt', { name }) : ''}
        className={`${sizeClass} rounded-full object-cover flex-shrink-0`}
      />
    );
  }

  const initial = (name || '?').trim()[0]?.toUpperCase() || '?';
  return (
    <div className={`${sizeClass} rounded-full bg-primary-lighter text-primary-dark flex items-center justify-center font-medium flex-shrink-0`}>
      {initial}
    </div>
  );
}

export default AdultAvatar;
